// Copyright (c) 2022 by Move Closer
import { AnyObject } from '@movecloser/front-core'

import {
  CategoryData,
  ContentSneakPeakData,
  DescriptionOfBaseFilter,
  ImageData,
  IRelatedService,
  isRelated,
  LinkData,
  NavigationItem,
  PossibleRelatedType,
  ProductData,
  Related,
  ResolvedBannerData
} from '../contexts'
import { logger } from '../support'

import { isUnresolvedLink, ResolvedLink, UnresolvedLink } from './partials'

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export async function resolveArticle (
  input: Related,
  relatedService: IRelatedService
): Promise<ContentSneakPeakData | null> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveArticles]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedArticle: ContentSneakPeakData | null = null

  try {
    resolvedArticle = await relatedService.resolve<ContentSneakPeakData>(input)
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedArticle
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export async function resolveArticles (
  input: Related,
  relatedService: IRelatedService
): Promise<ContentSneakPeakData[]> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveArticles]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedArticles: ContentSneakPeakData[] = []

  try {
    const articles = await relatedService.resolve<ContentSneakPeakData[]>(input)
    resolvedArticles = Array.isArray(articles) ? articles : []
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedArticles
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export async function resolveBanner (
  input: Related,
  relatedService: IRelatedService
): Promise<ResolvedBannerData | null> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveBanners]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedBanner: ResolvedBannerData | null = null

  try {
    resolvedBanner = await relatedService.resolve<ResolvedBannerData>(input)
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedBanner
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export async function resolveBanners (
  input: string[],
  relatedService: IRelatedService
): Promise<ResolvedBannerData[]> {
  let resolvedBanners: ResolvedBannerData[] = []

  try {
    const banners = await Promise.all(input.map(async value => {
      return await resolveBanner({ type: 'banners', value }, relatedService)
    }))

    resolvedBanners = Array.isArray(banners)
      ? banners.filter(b => b !== null) as ResolvedBannerData[]
      : []
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedBanners
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export async function resolveFilter (
  input: Related,
  relatedService: IRelatedService
): Promise<DescriptionOfBaseFilter | null> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveProducts]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedFilter: DescriptionOfBaseFilter | null = null

  try {
    resolvedFilter = await relatedService.resolve<DescriptionOfBaseFilter>(input)
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedFilter
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export async function resolveFilters (
  input: Related[],
  relatedService: IRelatedService
): Promise<DescriptionOfBaseFilter[]> {
  let resolvedFilters: DescriptionOfBaseFilter[] = []

  try {
    // todo: handle case when some are rejected
    const filters = await Promise.all(input.map(async f => {
      return await resolveFilter(f, relatedService)
    }))

    resolvedFilters = Array.isArray(filters)
      ? filters.filter(f => f !== null) as DescriptionOfBaseFilter[]
      : []
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedFilters
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export async function resolveImage (input: Related, relatedService: IRelatedService): Promise<ImageData> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveImage]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  if (input.type !== PossibleRelatedType.File) {
    throw new Error('[ResolveImage]: Given related is not a correct file related!')
  }

  return await relatedService.resolve<ImageData>(input)
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export async function resolveLink (input: UnresolvedLink, relatedService: IRelatedService): Promise<ResolvedLink> {
  if (!isUnresolvedLink(input)) {
    throw new Error(`[ResolveLink]: Provided [unresolvedLink] argument is NOT compliant with the [UnresolvedLink] interface!\n[unresolvedLink] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let label: string | undefined
  let target: string = input.target || '#'
  const { relatedTarget, ...linkData } = input

  if (relatedTarget) {
    try {
      const descriptionOfLink = await relatedService.resolve<LinkData>(relatedTarget)
      label = descriptionOfLink.title
      target = descriptionOfLink.url
    } catch (e) {
      logger(e, 'warn')
    }
  }

  return {
    ...linkData,
    label: input.label || label || '---',
    target
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export async function resolveNavigation<CanHaveEmptyTarget extends boolean = false> (input: Related, relatedService: IRelatedService): Promise<NavigationItem<CanHaveEmptyTarget>[]> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveNavigation]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let navigation: AnyObject = {}

  try {
    navigation = await relatedService.resolve<NavigationItem<CanHaveEmptyTarget>[]>(input)
  } catch (e) {
    logger(e, 'warn')
  }

  if (!Array.isArray(navigation.links)) {
    throw new Error(
      '[ResolveNavigation]: Resolved data is NOT compliant with the [NavigationItem[]] interface!')
  }

  return navigation.links
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export async function resolveProduct (
  input: Related,
  relatedService: IRelatedService
): Promise<ProductData | null> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveProducts]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedProduct: ProductData | null = null

  try {
    resolvedProduct = await relatedService.resolve<ProductData>(input)
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedProduct
}

/**
 * @author Katarzyna Otto <katarzyna.otto@movecloser.pl>
 */
export async function resolveProducts (
  input: Related,
  relatedService: IRelatedService
): Promise<ProductData[]> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveProducts]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedProducts: ProductData[] = []

  try {
    const products = await relatedService.resolve<ProductData[]>(input)
    resolvedProducts = Array.isArray(products) ? products : []
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedProducts
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export async function resolveCategory (
  input: Related,
  relatedService: IRelatedService
): Promise<CategoryData | null> {
  if (!isRelated(input)) {
    throw new Error(`[ResolveCategory]: Provided [input] argument is NOT compliant with the [Related] interface!\n[input] value: ${JSON.parse(
      JSON.stringify(input))}.`)
  }

  let resolvedCategory: CategoryData | null = null

  try {
    resolvedCategory = await relatedService.resolve<CategoryData>(input)
  } catch (e) {
    logger(e, 'warn')
  }

  return resolvedCategory
}
